import React from 'react';
import { useSelector } from 'react-redux';
import { Redirect, Route } from 'react-router-dom';
import { ERoutes } from '../../routes';
import { ApplicationState } from '../../store';
import { ECustomerTypeView } from '../../store/ducks/user/types';
import Fabrication from './Fabrication';
import SteelTypeChoose from './SteelTypeChoose';
import LongSteelRoutes from './LongSteel';
import PurchaseHome from './PurchaseHome';
import ReviewOrders from './Fabrication/ReviewOrder';
import useIsCommerceUser from '../../hooks/useIsCommerceUser';
import { GetFlag } from '../../utils/featureFlag';
import { hasOnlyCGCustomerTypes } from '../../utils/commerceUser';
import useIsBusinessTypeCG from '../../hooks/useIsBusinessTypeCG';

export enum EPurchaseRoutes {
  STEEL_TYPE_CHOOSE = 'steel-type-choose',
  LONG_STEEL = 'long-steel',
  FABRICATION = 'fabrication', // corte e dobra
  FABRICATION_REVIEW_ORDER = 'fabrication-review-order',
  COMMERCE = 'commerce',
  CHECKOUT = 'checkout'
}

const PurchaseRoutes: React.FC = () => {
  const path = `${ERoutes.PURCHASE}`;
  const customerViewType = useSelector(
    (appState: ApplicationState) => appState.user.searchCustomerType
  );
  const hasUserFabricationAccess = useSelector(
    (appState: ApplicationState) =>
      appState.user.userPermissions.hasFabricationAccess
  );
  const customers = useSelector(
    (appState: ApplicationState) => appState.customers.customers
  );
  const customersLoading = useSelector(
    (appState: ApplicationState) => appState.customers.loading
  );

  const isEmitter = customerViewType === ECustomerTypeView.EMITTER;
  const isFabricationReceiver =
    customerViewType === ECustomerTypeView.FABRICATION_RECEIVER;
  const isMVPUser = useIsCommerceUser();
  const migrationFlag = GetFlag('purchase-migration-brdi-brin');
  const isBusinessTypeCG = useIsBusinessTypeCG();
  const isOnlyCGEmitter =
    migrationFlag &&
    !customersLoading &&
    isEmitter &&
    hasOnlyCGCustomerTypes(customers);

  const CgOnlyFabricationRoutes = () => (
    <>
      <Route path={`${path}/`} exact>
        <Redirect
          to={{
            pathname: `${path}/${EPurchaseRoutes.COMMERCE}/${EPurchaseRoutes.STEEL_TYPE_CHOOSE}`,
            state: { cameFromPurchase: true }
          }}
        />
      </Route>
      <Route
        path={`${path}/${EPurchaseRoutes.COMMERCE}/${EPurchaseRoutes.STEEL_TYPE_CHOOSE}`}
        component={PurchaseHome}
      />
      <Route
        path={`${path}/${EPurchaseRoutes.FABRICATION}`}
        component={Fabrication}
      />
      <Route
        path={`${path}/${EPurchaseRoutes.FABRICATION_REVIEW_ORDER}`}
        component={ReviewOrders}
      />
    </>
  );

  const MVPRoutes = () => {
    return (
      <>
        <Route path={`${path}/`} exact>
          <Redirect
            to={{
              pathname: `${path}/${EPurchaseRoutes.COMMERCE}/${EPurchaseRoutes.STEEL_TYPE_CHOOSE}`,
              state: { cameFromPurchase: true }
            }}
          />
        </Route>

        <Route path={`${path}/${EPurchaseRoutes.STEEL_TYPE_CHOOSE}`}>
          <Redirect
            to={{
              pathname: `${path}/${EPurchaseRoutes.COMMERCE}/${EPurchaseRoutes.STEEL_TYPE_CHOOSE}`,
              state: { cameFromPurchase: true }
            }}
          />
        </Route>

        <Route
          path={`${path}/${EPurchaseRoutes.COMMERCE}/${EPurchaseRoutes.STEEL_TYPE_CHOOSE}`}
          component={PurchaseHome}
        />

        <Route
          path={`${path}/${EPurchaseRoutes.FABRICATION}`}
          component={Fabrication}
        />

        <Route
          path={`${path}/${EPurchaseRoutes.FABRICATION_REVIEW_ORDER}`}
          component={ReviewOrders}
        />

        <Route
          path={`${path}/${EPurchaseRoutes.LONG_STEEL}`}
          component={LongSteelRoutes}
        />
      </>
    );
  };

  const NonMvpRoutes = () => {
    return (
      <>
        <Route path={`${path}/`} exact>
          <Redirect to={`${path}${''}/${EPurchaseRoutes.STEEL_TYPE_CHOOSE}`} />
        </Route>

        <Route
          path={`${path}/${EPurchaseRoutes.COMMERCE}/${EPurchaseRoutes.STEEL_TYPE_CHOOSE}`}
        >
          <Redirect to={`${path}/${EPurchaseRoutes.STEEL_TYPE_CHOOSE}`} />
        </Route>

        <Route
          path={`${path}${''}/${EPurchaseRoutes.STEEL_TYPE_CHOOSE}`}
          component={SteelTypeChoose}
        />

        <Route
          path={`${path}${''}/${EPurchaseRoutes.FABRICATION}`}
          component={Fabrication}
        />

        <Route
          path={`${path}/${EPurchaseRoutes.FABRICATION_REVIEW_ORDER}`}
          component={ReviewOrders}
        />

        <Route path={`${path}/${EPurchaseRoutes.LONG_STEEL}`}>
          {isBusinessTypeCG ? (
            <Redirect to={`${path}/${EPurchaseRoutes.STEEL_TYPE_CHOOSE}`} />
          ) : (
            <LongSteelRoutes />
          )}
        </Route>
      </>
    );
  };

  return (
    <>
      {isEmitter && isMVPUser && <MVPRoutes />}

      {isOnlyCGEmitter && !isMVPUser && hasUserFabricationAccess && (
        <CgOnlyFabricationRoutes />
      )}

      {!migrationFlag &&
        isEmitter &&
        !isMVPUser &&
        hasUserFabricationAccess && <NonMvpRoutes />}

      {!migrationFlag &&
        isEmitter &&
        !isMVPUser &&
        !isBusinessTypeCG &&
        !hasUserFabricationAccess && (
          <>
            <Route path={`${path}/`} exact>
              <Redirect to={`${path}/${EPurchaseRoutes.LONG_STEEL}`} />
            </Route>

            <Route
              path={`${path}${''}/${EPurchaseRoutes.STEEL_TYPE_CHOOSE}`}
              exact
            >
              <Redirect to={`${path}/${EPurchaseRoutes.LONG_STEEL}`} />
            </Route>

            <Route
              path={`${path}/${EPurchaseRoutes.LONG_STEEL}`}
              component={LongSteelRoutes}
            />
          </>
        )}

      {hasUserFabricationAccess && isFabricationReceiver && <MVPRoutes />}
    </>
  );
};

export default PurchaseRoutes;
